import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { ArrowRight, LoaderCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { loginSchema, type AuthResponse, type LoginInput } from '@agenda-facil/contracts';
import { Button, Field, Input } from '@agenda-facil/ui';
import { api, ApiClientError } from '../../lib/api';
import { useAuthStore } from '../../stores/auth-store';
import { AuthShell } from './auth-shell';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState('');
  const setSession = useAuthStore((state) => state.setSession);
  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });
  const login = useMutation({
    mutationFn: (input: LoginInput) => api.post<AuthResponse>('/auth/login', input),
    onSuccess: (result) => {
      setSession(result.accessToken, result.user);
      const from = (location.state as { from?: string } | null)?.from;
      const destination =
        result.user.role === 'SUPER_ADMIN' ? '/admin' : result.user.shopId ? '/app' : '/onboarding';
      void navigate(from ?? destination, { replace: true });
    },
    onError: (caught) =>
      setError(caught instanceof ApiClientError ? caught.message : 'Não foi possível entrar.'),
  });

  return (
    <AuthShell
      title="Bom ter você de volta."
      subtitle="Entre para acompanhar a agenda e o movimento da sua barbearia."
    >
      <form
        className="grid gap-5"
        onSubmit={(event) => {
          void form.handleSubmit((input) => {
            setError('');
            login.mutate(input);
          })(event);
        }}
      >
        <Field label="E-mail" htmlFor="email" error={form.formState.errors.email?.message}>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="voce@barbearia.com.br"
            {...form.register('email')}
          />
        </Field>
        <Field label="Senha" htmlFor="password" error={form.formState.errors.password?.message}>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            placeholder="Sua senha"
            {...form.register('password')}
          />
        </Field>
        {error && (
          <p
            className="rounded-xl bg-[var(--danger-soft)] p-3 text-sm font-bold text-[var(--danger)]"
            role="alert"
          >
            {error}
          </p>
        )}
        <div className="flex justify-end">
          <Link className="text-sm font-bold text-[var(--accent)]" to="/recuperar-senha">
            Esqueci minha senha
          </Link>
        </div>
        <Button size="lg" disabled={login.isPending}>
          {login.isPending ? (
            <>
              <LoaderCircle className="animate-spin" size={18} /> Entrando…
            </>
          ) : (
            <>
              Entrar <ArrowRight size={18} />
            </>
          )}
        </Button>
      </form>
      <p className="mt-7 text-center text-sm text-[var(--muted-foreground)]">
        Ainda não tem uma conta?{' '}
        <Link className="font-extrabold text-[var(--foreground)]" to="/cadastro">
          Comece grátis
        </Link>
      </p>
    </AuthShell>
  );
}
